import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../common/Loader';
import { openMediaViewModal } from '../../actions/modal';
import { getServiceRequestById } from '../../actions/service_request';
import { getPavMilestoneAssets, getPavMilestoneLabel, isPavMilestone } from './pavConfig';
import { getMediaViewAttachments, getSavedAssetIdentity } from './pavAssetUtils';
import PavAssetReviewCard, { PAV_ASSET_GRID_CLASS } from './PavAssetReviewCard';
import usePavAssetVerification from './usePavAssetVerification';

// Milestone-wise review of every PAV asset and its attachments, with a physical
// verification approve/reject per asset. Opened from the ticket actions menu.
export default function PavPhysicalVerification({ ticket }) {
    const dispatch = useDispatch();
    const ticketId = ticket?.id || ticket?.ticket_id;
    const refreshedTicket = useSelector((state) => state.serviceRequest.serviceRequestData?.[ticketId]);
    const isTicketLoading = useSelector((state) => state.loading.isServiceRequestByIdLoading);
    const activeTicket = refreshedTicket || ticket;
    const { verifyAsset, getAssetOutcome, getPendingDecision } = usePavAssetVerification(ticketId);

    // The ticket handed over by the list does not always carry its assets and
    // their attachments, so the full detail is pulled once when this opens.
    useEffect(() => {
        if (ticketId) dispatch(getServiceRequestById({ ticketId, callDetail: true }));
    }, [dispatch, ticketId]);

    const milestoneSections = useMemo(() => (
        (Array.isArray(activeTicket?.milestones) ? activeTicket.milestones : [])
            .filter(isPavMilestone)
            .map((milestone) => ({
                milestone,
                assets: getPavMilestoneAssets(activeTicket, milestone),
            }))
    ), [activeTicket]);

    const totalAssets = milestoneSections.reduce((count, section) => count + section.assets.length, 0);

    const previewAttachments = useCallback((attachments, openedIndex) => {
        const mediaAttachments = getMediaViewAttachments(attachments);
        if (mediaAttachments.length === 0) return;
        dispatch(openMediaViewModal({
            attachments: mediaAttachments,
            openedMediaIndex: Math.max(openedIndex, 0),
            showSenderDetail: false,
        }));
    }, [dispatch]);

    return (
        <div className='flex max-h-[70vh] min-h-0 flex-col bg-scogof8'>
            <div className='shrink-0 border-b border-scogoddd bg-white px-4 py-2.5'>
                <div className='flex flex-wrap items-center gap-2'>
                    <span className='material-icons-outlined text-font16 text-scogoprimary'>fact_check</span>
                    <p className='min-w-0 flex-1 text-font12 font-semibold text-scogo21'>
                        Review the attachments of every PAV milestone, then approve or reject each asset.
                    </p>
                    <span className='shrink-0 rounded-full border border-scogoddd bg-scogof8 px-2.5 py-1 text-font10 font-semibold text-slate-600'>
                        {milestoneSections.length} milestone{milestoneSections.length === 1 ? '' : 's'} · {totalAssets} asset{totalAssets === 1 ? '' : 's'}
                    </span>
                </div>
            </div>

            <div className='min-h-0 flex-1 overflow-y-auto p-3'>
                {isTicketLoading && totalAssets === 0 ? (
                    <div className='flex items-center justify-center gap-2 rounded-lg border border-scogoddd bg-white px-4 py-6 text-font11 font-normal text-slate-600'>
                        <Loader size='16' thickness='2' color='#12355F' />
                        Loading milestone assets...
                    </div>
                ) : milestoneSections.length === 0 ? (
                    <p className='rounded-lg border border-dashed border-scogoddd bg-white px-4 py-6 text-center text-font11 font-normal text-slate-600'>This ticket has no PAV milestone.</p>
                ) : (
                    <div className='space-y-3'>
                        {milestoneSections.map(({ milestone, assets }) => (
                            <section key={milestone.id} className='overflow-hidden rounded-xl border border-scogoddd bg-white shadow-sm'>
                                <div className='flex flex-wrap items-center gap-2 border-b border-scogoddd bg-scogof8 px-3 py-2'>
                                    <span className='flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-scogolightblue text-scogoprimary'>
                                        <span className='material-icons-outlined text-font14'>verified</span>
                                    </span>
                                    <div className='min-w-0 flex-1'>
                                        <p className='truncate text-font12 font-semibold text-scogo21'>{getPavMilestoneLabel(milestone)}</p>
                                        <p className='truncate text-font10 font-normal text-slate-600'>{milestone?.milestone_status || 'PAV milestone'}</p>
                                    </div>
                                    <span className='shrink-0 rounded-full border border-scogoddd bg-white px-2 py-0.5 text-font10 font-semibold text-slate-600'>
                                        {assets.length} asset{assets.length === 1 ? '' : 's'}
                                    </span>
                                </div>
                                <div className={`p-2 ${assets.length === 0 ? '' : PAV_ASSET_GRID_CLASS}`}>
                                    {assets.length === 0 ? (
                                        <p className='rounded-md border border-dashed border-scogoddd bg-scogof8 px-3 py-4 text-center text-font10 font-normal text-slate-600'>No assets have been added to this milestone yet.</p>
                                    ) : assets.map((asset, index) => {
                                        const assetKey = `${milestone.id}-${getSavedAssetIdentity(asset, index)}`;
                                        return (
                                            <PavAssetReviewCard
                                                key={assetKey}
                                                asset={asset}
                                                assetKey={assetKey}
                                                milestoneId={milestone?.id}
                                                isNarrow
                                                canVerify
                                                outcome={getAssetOutcome(asset)}
                                                pendingDecision={getPendingDecision(assetKey)}
                                                onVerify={verifyAsset}
                                                onPreview={previewAttachments}
                                            />
                                        );
                                    })}
                                </div>
                            </section>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
